import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { QuestionField } from "@/components/forms/QuestionField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 py-10 px-4">
    <div className="max-w-2xl mx-auto space-y-4">{children}</div>
  </div>
);

export default function FormFill() {
  const { id } = useParams();
  const { branding } = useBranding();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/forms/fill/${id}`).then((r) => setForm(r.data))
      .catch((e) => setError({ status: e.response?.status, msg: e.response?.data?.detail || "Form tidak ditemukan" }));
  }, [id]);

  const submit = async () => {
    const miss = form.questions.find((q) => q.required && (answers[q.id] === undefined || answers[q.id] === "" || (Array.isArray(answers[q.id]) && !answers[q.id].length)));
    if (miss) return toast.error(`"${miss.label}" wajib diisi`);
    setSending(true);
    try { await api.post(`/forms/fill/${id}/submit`, { answers, respondent_name: name }); setDone(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal mengirim"); }
    setSending(false);
  };

  if (error) return (
    <Shell><div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="fill-error">
      <Lock className="h-10 w-10 mx-auto text-indigo-300 mb-3" /><p className="text-slate-700 font-semibold">{error.msg}</p>
      {error.status === 401 && <Link to={`/login?next=/f/${id}`} className="inline-block mt-4 text-indigo-600 font-semibold hover:underline" data-testid="fill-login-link">Masuk ke aplikasi</Link>}
    </div></Shell>
  );
  if (!form) return <Shell><div className="text-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div></Shell>;
  if (done) return (
    <Shell><div className="rounded-2xl bg-white border border-slate-200 border-t-8 border-t-emerald-500 p-8 text-center" data-testid="fill-success">
      <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
      <h2 className="text-xl font-extrabold text-slate-900">{form.title}</h2>
      <p className="text-slate-500 mt-1">Terima kasih, jawaban Anda telah terekam.</p>
      <button onClick={() => { setAnswers({}); setDone(false); }} className="mt-4 text-indigo-600 font-semibold hover:underline" data-testid="fill-again-btn">Kirim jawaban lain</button>
    </div></Shell>
  );

  return (
    <Shell>
      <div className="rounded-2xl bg-white border border-slate-200 border-t-8 border-t-indigo-500 p-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 mb-1">{branding?.app_name || "ZOA Mission Control"}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="fill-title">{form.title}</h1>
        {form.description && <p className="text-slate-600 mt-2 whitespace-pre-line">{form.description}</p>}
        <p className="text-xs text-slate-400 mt-3">{form.respondent ? `Mengisi sebagai ${form.respondent.name} (${form.respondent.email})` : "* menandakan pertanyaan wajib"}</p>
      </div>
      {!form.is_open ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 text-center text-slate-600" data-testid="fill-closed">Form ini sudah tidak menerima jawaban.</div>
      ) : (
        <>
          {!form.respondent && (
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="font-semibold text-slate-800 mb-2">Nama Anda</p>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama (opsional)" data-testid="fill-name-input" className="max-w-sm" />
            </div>
          )}
          {form.questions.map((q, i) => (
            <div key={q.id} className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="font-semibold text-slate-800">{q.label}{q.required && <span className="text-rose-500"> *</span>}</p>
              {q.description && <p className="text-sm text-slate-500 mt-0.5">{q.description}</p>}
              <div className="mt-3"><QuestionField q={q} idx={i} formId={id} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} /></div>
            </div>
          ))}
          <div className="flex justify-between items-center">
            <Button onClick={submit} disabled={sending} data-testid="fill-submit-btn" className="bg-indigo-600 hover:bg-indigo-700 px-8">{sending ? "Mengirim..." : "Kirim"}</Button>
            <button onClick={() => setAnswers({})} className="text-sm text-slate-500 hover:text-slate-700" data-testid="fill-clear-btn">Kosongkan form</button>
          </div>
        </>
      )}
    </Shell>
  );
}
